import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { user } from 'src/backend';
import { NoInvitesError } from 'src/backend/user';

// ----------------------------------------------------------------------

/**
 * @typedef Invites
 * @property {Invite[]} invites
 * @property {boolean} isLoading
 * @property {Function} invalidate
 */

/**
 * Encapsulates the user's pending invites state management & logic, returning
 * that logic's state.
 * Note: invites is never null, but an empty array if there is no data.
 * @param {string | null} email
 * @param {object} [options] Proof of owning `email` when there is no session.
 * @param {string} [options.emailProof] From `auth.verifyEmailCode`.
 * @param {string} [options.token] Invite token from the invite email link.
 * @param {boolean} [options.skip] Treat as "no invites" without asking the API.
 * @returns {Invites}
 */
export default function useInvites(email, { emailProof, token, skip } = {}) {
  const { t } = useTranslation('translation');
  const { enqueueSnackbar } = useSnackbar();
  const [cachedInvites, setCachedInvites] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (cachedInvites) return;
    if (!email) return;
    if (skip) {
      setCachedInvites([]);
      return;
    }

    user
      .getInvites(email, { emailProof, token })
      .then((invites) => {
        if (!isMounted) return;
        if (!Array.isArray(invites)) {
          setCachedInvites([]);
          return;
        }
        setCachedInvites(invites);
      })
      .catch((error) => {
        if (!isMounted) return;
        setCachedInvites([]);
        if (error instanceof NoInvitesError) {
          enqueueSnackbar(
            t('auth.mobile.pending-invites.no-invites-contact-admin'),
            {
              variant: 'warning',
            }
          );
        } else {
          enqueueSnackbar(
            'An error occurred while trying to fetch your invites. Please try again later.',
            { variant: 'error' }
          );
        }
      });

    return () => {
      isMounted = false;
    };
  }, [enqueueSnackbar, email, cachedInvites, emailProof, token, skip]);

  const invalidate = () => setCachedInvites(null);

  return {
    invites: cachedInvites ?? [],
    isLoading: cachedInvites === null,
    invalidate,
  };
}
